import { dateFormatInUserTimeZone, formatTime } from '@/helpers/common';
import { AbstractModel } from '@/models/abstractModel';
import { useBookmakerDictionary, useCountryDictionary } from '@/stores';
import type { IMonitoringBookmaker, ParsingStatusDisplay } from '@/types';

export class MonitoringBookmaker extends AbstractModel {
    public accountActiveCount: number;
    public accountCount: number;
    public accountProxyCount: number;
    public accountProxyCountryIds: number[];
    public activeAccParseCount: number;
    public comment?: string;
    public earlyBasketballLastUpdatedAt?: string;
    public earlyBasketballStatus?: 1 | 0;
    public earlyFootballLastUpdatedAt?: string;
    public earlyFootballStatus?: 1 | 0;
    public isShowAlerts: number;
    public liveBasketballLastUpdatedAt?: string;
    public liveBasketballStatus?: 1 | 0;
    public liveFootballLastUpdatedAt?: string;
    public liveFootballStatus?: 1 | 0;
    public name: string;
    public siteId: number;
    public status: boolean;
    public todayBasketballLastUpdatedAt?: string;
    public todayBasketballStatus?: 1 | 0;
    public todayFootballLastUpdatedAt?: string;
    public todayFootballStatus?: 1 | 0;
    public totalAccParseCount: number;

    constructor(data: IMonitoringBookmaker) {
        super();
        this.siteId = data.site_id;
        this.isShowAlerts = data.is_show_alerts;
        this.name = data.name;
        this.status = data.status;
        this.comment = data.comment;
        this.accountCount = data.account_count;
        this.activeAccParseCount = data.active_acc_parse_count;
        this.totalAccParseCount = data.total_acc_parse_count;
        this.accountActiveCount = data.account_active_count;
        this.accountProxyCount = data.account_proxy_count;
        this.accountProxyCountryIds = data.account_proxy_country_ids;
        this.liveFootballStatus = data.live_football_status;
        this.liveFootballLastUpdatedAt = data.live_football_last_updated_at;
        this.liveBasketballStatus = data.live_basketball_status;
        this.liveBasketballLastUpdatedAt = data.live_basketball_last_updated_at;
        this.todayFootballStatus = data.today_football_status;
        this.todayFootballLastUpdatedAt = data.today_football_last_updated_at;
        this.todayBasketballStatus = data.today_basketball_status;
        this.todayBasketballLastUpdatedAt = data.today_basketball_last_updated_at;
        this.earlyFootballStatus = data.early_football_status;
        this.earlyFootballLastUpdatedAt = data.early_football_last_updated_at;
        this.earlyBasketballStatus = data.early_basketball_status;
        this.earlyBasketballLastUpdatedAt = data.early_basketball_last_updated_at;
    }

    get accountsDisplay(): string {
        return `${this.activeAccParseCount} | ${this.totalAccParseCount}`;
    }

    get bookmakerName(): string {
        try {
            const bookmakerDictionary = useBookmakerDictionary();
            return bookmakerDictionary.nameById(this.siteId) || '';
        } catch {
            return '';
        }
    }

    get countryCodesDisplay(): string {
        if (!this.accountProxyCountryIds || this.accountProxyCountryIds.length === 0) return '';
        try {
            const countryDictionary = useCountryDictionary();
            return this.accountProxyCountryIds
                .map((id) => {
                    try {
                        const country = countryDictionary.itemById(id);
                        return country?.iso3?.toUpperCase() || '';
                    } catch {
                        return '';
                    }
                })
                .filter((code) => code)
                .join(', ');
        } catch {
            return '';
        }
    }

    get earlyBasketballDisplay(): ParsingStatusDisplay | null {
        return this.buildParsingStatusDisplay(this.earlyBasketballStatus, this.earlyBasketballLastUpdatedAt);
    }

    get earlyFootballDisplay(): ParsingStatusDisplay | null {
        return this.buildParsingStatusDisplay(this.earlyFootballStatus, this.earlyFootballLastUpdatedAt);
    }

    get liveBasketballDisplay(): ParsingStatusDisplay | null {
        return this.buildParsingStatusDisplay(this.liveBasketballStatus, this.liveBasketballLastUpdatedAt);
    }

    get liveFootballDisplay(): ParsingStatusDisplay | null {
        return this.buildParsingStatusDisplay(this.liveFootballStatus, this.liveFootballLastUpdatedAt);
    }

    get statusClass(): string {
        if (
            this.status &&
            this.liveFootballStatus !== 0 &&
            this.liveBasketballStatus !== 0 &&
            this.todayFootballStatus !== 0 &&
            this.todayBasketballStatus !== 0 &&
            this.earlyFootballStatus !== 0 &&
            this.earlyBasketballStatus !== 0 &&
            this.accountActiveCount > 0
        ) {
            return 'status-green';
        }
        return 'status-red';
    }

    get todayBasketballDisplay(): ParsingStatusDisplay | null {
        return this.buildParsingStatusDisplay(this.todayBasketballStatus, this.todayBasketballLastUpdatedAt);
    }

    get todayFootballDisplay(): ParsingStatusDisplay | null {
        return this.buildParsingStatusDisplay(this.todayFootballStatus, this.todayFootballLastUpdatedAt);
    }

    public buildParsingStatusDisplay(status: 1 | 0 | undefined, lastUpdatedAt?: string): ParsingStatusDisplay | null {
        if (!lastUpdatedAt || (status !== 1 && status !== 0)) return null;

        return {
            text: formatTime(lastUpdatedAt),
            class: status === 1 ? 'text-tbGreen' : 'text-tbRed',
            tooltip: dateFormatInUserTimeZone(lastUpdatedAt, 'dd-MM-yyyy'),
        };
    }
}
